import React, { useRef, useCallback, useMemo, useEffect, useState, Fragment } from "react";
import { useSearchParams } from "react-router-dom";

import placeholderFormat from "common/placeholder-format";
import COMMON from "common";
import api from "services/api";
import sanitizeObject from "common/sanitize-object";
import serveRequestErrors from "common/serve-request-errors";
import AppEmptyState from "components/app-empty-state";
import AppTable, { AppTableCell, AppTableEditIcon } from "components/app-table";
import pdfIcon from "assets/images/pdf-icon.svg";
import AppCreateAssignmentModal from "components/pages/e-learning/app-create-assignment-modal";
import { useFormik } from "formik";
import filterIcon from "assets/images/components/app-multi-select-input/filter-icon.svg";
import exportIcon from "assets/images/pages/user-management/export-icon.svg";
import uploadIcon from "assets/images/upload-icon.svg";
import AppSearchInput from "components/app-search-input";
import AppInput from "components/app-input";
import AppSelectInput from "components/app-select-input";
import AppButton from "components/app-button";
import queryParamsEntries from "common/query-params-entries";
import AppStatus from "components/app-status";
import ERRORS from "common/errors";
import * as yup from "yup";
import { CALENDAR_FORMAT, formatDate, formatDatePattern, isDateBeforeToday, isEndDateBeforeStartDate } from "common/calendar";
import AppCalendarInput from "components/app-calendar-input";
import AppCheckbox from "components/app-checkbox";
import AppUploadCertModal from "components/pages/e-learning/app-upload-cert-modal";

const PageAssignments = (props) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const memoSetSearchParams = useRef(setSearchParams);
    const createAssignmentRef = useRef();
    const uploadCertificateRef = useRef();
    const searchInputRef = useRef();
    const paramsRef = useRef({
        page: parseInt(searchParams.get("page")) || 1, sort: searchParams.get("sort") || "",
        assignee: searchParams.get("assignee") || "",
        title: searchParams.get("title") || "",
        submissionDateFrom: searchParams.get("submissionDateFrom") || "",
        submissionDateTo: searchParams.get("submissionDateTo") || "",
        assignmentStatus: searchParams.get("assignmentStatus") || "",
        certificateStatus: searchParams.get("certificateStatus") || "",
    });
    const [data, setData] = useState({ page: paramsRef.current.page, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });
    const [isExporting, setIsExporting] = useState(false);
    const cancelRequest = useMemo(() => props.onHandleCancelRequest, [props.onHandleCancelRequest]);
    const isEmptyState = useMemo(() => data.status === true, [data.status]);
    const initialValues = useMemo(() => ({ assignee: "", title: "", certificateStatus: "", assignmentStatus: "", submissionDateFrom: "", submissionDateTo: "" }), []);
    const formik = useFormik({
        initialValues,
        validationSchema: yup.object({
            submissionDateFrom: yup.string().test("submissionDateFrom", ERRORS.START_DATE, function (value) {
                if (!value || !this.parent.submissionDateTo)
                    return true;

                return isDateBeforeToday(value, this.parent.submissionDateTo);
            }),
            submissionDateTo: yup.string().test("submissionDateTo", ERRORS.END_DATE, function (value) {
                if (!value || !this.parent.submissionDateFrom)
                    return true;

                return isDateBeforeToday(this.parent.submissionDateFrom || new Date(), value);
            }),
        }),
        onSubmit: (values) => {
            onHandleSubmitSearch(values);
        },
    });

    //prettier-ignore
    const onHandleSubmissionDateFrom = useCallback((name, value) => {
        formik.setFieldValue(name, value);

        if (isEndDateBeforeStartDate(value, formik.values.submissionDateTo)) formik.setFieldValue("submissionDateTo", "");
    }, [formik]);

    const calendarDisplayFormat = CALENDAR_FORMAT.DATE_FORMAT + " " + CALENDAR_FORMAT.MONTH_FORMAT + " " + CALENDAR_FORMAT.YEAR_FORMAT;

    const onHandleSubmitSearch = (values) => {
        searchInputRef.current.onhandleCloseAdvanceSearch();

        paramsRef.current = { ...paramsRef.current, page: 1, ...values };

        onHandleGetList();
    };

    //prettier-ignore
    const onHandleResetSearch = () => {
        formik.setValues(formik.initialValues);

        setVisibleColumns(columns.map((col) => col.accessor));

        paramsRef.current = { page: 1, sort: "", ...formik.initialValues };

        onHandleGetList();
    };


    const onHandleGetList = useCallback(async () => {
        let response = null;

        setData({ page: paramsRef.current.page, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });

        try {
            const payload = { ...paramsRef.current, size: 10 };

            memoSetSearchParams.current(sanitizeObject(payload), { replace: true });

            payload.page = paramsRef.current.page - 1;

            if (payload.submissionDateFrom) {
                payload.submissionDateFrom = formatDatePattern(new Date(payload.submissionDateFrom), CALENDAR_FORMAT.CALENDAR_FORMAT);
            }

            if (payload.submissionDateTo) {
                payload.submissionDateTo = formatDatePattern(new Date(payload.submissionDateTo), CALENDAR_FORMAT.CALENDAR_FORMAT);
            }

            response = await api.get.assignment.list(sanitizeObject(payload));
        } catch (error) {
            serveRequestErrors(error);
        }

        if (response) {
            setData((prev) => ({
                ...prev,
                page: paramsRef.current.page,
                prev: !response.first,
                next: !response.last,
                items: response.content,
                total: response.totalPages,
                totalElements: response.totalElements,
                offset: response?.pageable?.offset || 0,
                status: response?.pageable,
            }));
        }
    }, []);

    //prettier-ignore
    const onHandlePagination = useCallback((event) => {
        const control = event.currentTarget?.getAttribute("data-ctrl");

        if (control) {
            if (control === "prev") {
                if (paramsRef.current.page <= 1) return;
                paramsRef.current.page -= 1;
            } else {
                if (paramsRef.current.page >= data.total) return;
                paramsRef.current.page += 1;
            }
        } else {
            paramsRef.current.page = event.target.value;
        }

        onHandleGetList();
    }, [onHandleGetList, data.total]);

    //prettier-ignore
    const onHandleSort = useCallback((id, order) => {
        paramsRef.current.sort = order ? id + "," + order : "";

        paramsRef.current.page = 1;

        onHandleGetList();
    }, [onHandleGetList]);

    const onHandleCreate = useCallback(() => {
        createAssignmentRef.current?.onHandleShow();
    }, []);

    //prettier-ignore
    const onHandleUpload = useCallback((row) => {
        uploadCertificateRef.current?.onHandleShow(row.id);
    }, []);

    //prettier-ignore
    const AttachmentCell = useCallback(({ row }) => {
        const file = row.original?.certificateVO;
        const value = file ? (<a href={file?.filePath} target="_blank" rel="noopener noreferrer"><img className="table__file" src={pdfIcon} alt="attachment" /></a>) : ("-");
        return (
            <AppTableCell center value={value} />
        );
    }, []);

    //prettier-ignore
    const NameCell = useCallback(({ row }) => {
        return (
            <div className="name-column">
                <AppTableCell left value={row.original.employeeName} />
            </div>)
    }, []);

    //prettier-ignore
    const SubmissionDateCell = useCallback(({ row }) => {
        const date = row.original?.submissionDate;
        return (<AppTableCell center value={date ? formatDate(date) : "-"} />);
    }, []);

    //prettier-ignore
    const AssignmentCell = useCallback(({ row }) => {
        const status = row.original?.status;
        return (<AppStatus status={status ? status : "-"} />);
    }, []);

    //prettier-ignore
    const CertificateCell = useCallback(({ row }) => {
        const status = row.original?.certificateVO?.status;
        return (<AppStatus status={status ? status : "-"} />);
    }, []);

    //prettier-ignore
    const MenuCell = useCallback(({ row }) => {
        if (row.original.status === COMMON.STATUS_ID.OVERDUE) {
            return <AppTableEditIcon icon={uploadIcon} onClick={() => onHandleUpload(row.original)} />
        };
    }, [onHandleUpload]);

    //prettier-ignore
    const columns = useMemo(() => [
        {
            Header: "Assignee",
            accessor: "employeeName",
            disableSortBy: false,
            Cell: NameCell,
        },
        {
            Header: "Course",
            accessor: "courseTitle",
            disableSortBy: false,
        },
        {
            Header: "Assigned By",
            accessor: "assignedByName",
            disableSortBy: false,
        },
        {
            Header: "Submitted On",
            accessor: "submissionDate",
            disableSortBy: false,
            Cell: SubmissionDateCell,
        },
        {
            Header: "Status",
            accessor: "status",
            disableSortBy: false,
            Cell: AssignmentCell,
        },
        {
            Header: "Attachment",
            accessor: "attachment",
            disableSortBy: true,
            Cell: AttachmentCell,
        },
        {
            Header: "Certificate Status",
            accessor: "certificateVO.status",
            disableSortBy: false,
            Cell: CertificateCell,
        },
        {
            Header: "",
            accessor: "*",
            disableSortBy: true,
            Cell: MenuCell,
        }
    ], [AttachmentCell, AssignmentCell, CertificateCell, NameCell, SubmissionDateCell, MenuCell]);

    const [visibleColumns, setVisibleColumns] = useState(columns.map((col) => col.accessor));

    //prettier-ignore
    const advanceSearchValues = useMemo(() => {
        const params = queryParamsEntries(searchParams);
        let values = {
            assignee: params.assignee,
            title: params.title,
            certificateStatus: params.certificateStatus,
            assignmentStatus: params.assignmentStatus,
            submissionDateFrom: params.submissionDateFrom,
            submissionDateTo: params.submissionDateTo,
        };

        return values;
    }, [searchParams]);

    const toggleColumnVisibility = (accessor) => {
        setVisibleColumns((prev) => {
            if (!prev.includes(accessor)) return [...prev, accessor];

            const next = prev.filter((val) => val !== accessor);
            const hasDataColumn = next.some((val) => val && val !== "*" && val !== "**");

            return hasDataColumn ? next : prev;
        });
    };

    //prettier-ignore
    const onHandleRemoveField = (field) => {
        formik.setFieldValue(field, "");
        paramsRef.current = { ...paramsRef.current, page: 1, [field]: "" };
        onHandleGetList();
    };

    //prettier-ignore
    const onHandleShowAdvanceSearch = (event) => {
        if (searchInputRef.current?.onHandleShowAdvanceSearch) {
            searchInputRef.current.onHandleShowAdvanceSearch(event);
        }
    };

    const onHandleExport = useCallback(async () => {
        setIsExporting(true);

        const visibleColumnsFiltered = visibleColumns.filter((col) => col !== "*" && col !== "**");

        const visibleHeaders = columns.filter((col) => visibleColumnsFiltered.includes(col.accessor)).map((col) => col.Header);

        const values = { ...paramsRef.current }

        let response = null;
        let fileName = null;

        let sortFilter = values.sort;
        if (sortFilter) {
            const [sortColumn] = sortFilter.split(",");
            if (!visibleColumnsFiltered.includes(sortColumn)) {
                sortFilter = null;
            }
        }

        try {
            const payload = {
                headers: visibleHeaders,
                columnFilters: visibleColumnsFiltered,
                searchFilters: {
                    employeeName: values.assignee,
                    courseTitle: values.title,
                    submissionDateFrom: values.submissionDateFrom,
                    submissionDateTo: values.submissionDateTo,
                    status: values.assignmentStatus,
                    certificateStatus: values.certificateStatus,
                },
                sortFilter: sortFilter,
            }

            const transformResponse = (data, headers) => {
                fileName = headers?.["content-disposition"]?.split("attachment; filename=")?.[1];

                if (fileName) return data;

                try {
                    const jsonResponse = JSON.parse(new TextDecoder().decode(data));
                    if (jsonResponse) return jsonResponse;
                } catch (error) {
                    return data;
                }
            };

            response = await api.post.assignment.export(payload, { transformResponse });
        } catch (error) {
            serveRequestErrors(error);
        } finally {
            setIsExporting(false);
        }

        if (response) {
            const a = document.createElement("a");
            document.body.appendChild(a);
            const url = window.URL.createObjectURL(new Blob([response]), { type: "application/octet-stream" });
            a.href = url;
            a.download = fileName;
            a.click();
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            }, 1000);
        }

    }, [visibleColumns, columns]);

    //prettier-ignore
    const headerButtons = useMemo(() => {
        const buttons = [
            { label: "Filter", icon: filterIcon, outline: true, onClick: onHandleShowAdvanceSearch },
            { label: "Export", icon: exportIcon, outline: true, onClick: onHandleExport, disabled: isExporting, showLoadingDots: isExporting },

        ];
        return buttons;
    }, [onHandleExport, isExporting]);


    useEffect(() => {
        onHandleGetList();
    }, [onHandleGetList]);

    useEffect(() => {
        return () => {
            cancelRequest(COMMON.ENDPOINT_PATH.ASSIGNMENT.LIST);
        };
    }, [cancelRequest]);

    return (
        <div className="page-assignments">
            <div className="assignments">
                <AppSearchInput ref={searchInputRef} multiValues={advanceSearchValues} onRemoveField={onHandleRemoveField} buttons={headerButtons}>
                    <form className="app-advance-search-form" onSubmit={formik.handleSubmit}>
                        <div className="advance-form">
                            <div className="advance-form__inputs">
                                <span className="advance-form__inputs__toggle-word">Select Columns to Display:</span>
                                <div className="advance-form__inputs__checkbox">
                                    {columns
                                        .filter((col) => col.accessor && col.accessor !== "*" && col.accessor !== "**")
                                        .map((col) => (
                                            <div key={col.accessor}>
                                                <AppCheckbox
                                                    key={col.accessor}
                                                    name={col.accessor}
                                                    value={visibleColumns.includes(col.accessor)}
                                                    onClick={() => toggleColumnVisibility(col.accessor)}
                                                    label={col.Header}
                                                />
                                            </div>
                                        ))}
                                </div>
                                <AppInput type="text" name="assignee" label="Assignee Name" placeholder="Enter assignee name" value={formik.values.assignee} onChange={formik.handleChange} />

                                <AppInput type="text" name="title" label="Course Title" placeholder="Enter course title" value={formik.values.title} onChange={formik.handleChange} />

                                <AppCalendarInput name="submissionDateFrom" label="Submission Date From" placeholder={placeholderFormat} displayFormat={calendarDisplayFormat} value={formik.values.submissionDateFrom} error={formik.errors.submissionDateFrom} touched={formik.touched.submissionDateFrom} onChange={onHandleSubmissionDateFrom} />

                                <AppCalendarInput name="submissionDateTo" label="Submission Date To" placeholder={placeholderFormat} displayFormat={calendarDisplayFormat} minDate={formik.values.submissionDateFrom} value={formik.values.submissionDateTo} error={formik.errors.submissionDateTo} touched={formik.touched.submissionDateTo} onChange={formik.setFieldValue} />

                                <AppSelectInput name="assignmentStatus" label="Assignment Status" placeholder="Select Assignment Status" options={COMMON.DROPDOWNS.ASSIGNMENT_HISTORY} value={formik.values.assignmentStatus} onChange={formik.handleChange} />

                                <AppSelectInput name="certificateStatus" label="Certificate Status" placeholder="Select Certificate Status" options={COMMON.DROPDOWNS.CERTIFICATE} value={formik.values.certificateStatus} onChange={formik.handleChange} />
                            </div>

                            <div className="advance-form__button-container">
                                <AppButton type="button" label="Clear" outline onClick={onHandleResetSearch} />
                                <AppButton type="submit" label="Search" />
                            </div>
                        </div>
                    </form>
                </AppSearchInput>
                {isEmptyState && <AppEmptyState title="No records found" description="You don’t have any records yet" label="Add New Benefit" onClick={onHandleCreate} />}

                {!isEmptyState && (
                    <Fragment>
                        <AppTable columns={columns.filter(col => visibleColumns.includes(col.accessor))} pages={data} onHandlePagination={onHandlePagination} onHandleSort={onHandleSort} />
                    </Fragment>
                )}
            </div>

            <AppCreateAssignmentModal ref={createAssignmentRef} onHandleGetList={onHandleGetList} />
            <AppUploadCertModal ref={uploadCertificateRef} onHandleGetList={onHandleGetList} />
        </div>
    );
};

export default PageAssignments;
